import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import { getBoxUploadStatus, type BoxFileStatus } from '../../../../../../services/documentService';
import { logger } from '../../../../../../services/loggingService';
import { useViewerStore } from '../../../../../../store/store';

function formatLocalTime(utc: string): string {
    const date = new Date(utc);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });
}

function boxFileVersion(etag: string): string {
    const n = Number(etag);
    if (!Number.isFinite(n)) return etag || '';
    return String(n + 1);
}

type BoxFilesModalProps = {
    open: boolean;
    onClose: () => void;
    opportunityId: string;
};

const BoxFilesModal: React.FC<BoxFilesModalProps> = ({ open, onClose, opportunityId }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [files, setFiles] = useState<BoxFileStatus[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        useViewerStore.setState({ isModalOpenInInterface: open });
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (!opportunityId) {
            setFiles([]);
            setError('Unable to load files from Box.');
            setIsLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setError(null);
            setFiles([]);
            try {
                const data = await getBoxUploadStatus(opportunityId);
                if (!cancelled) {
                    setFiles(data);
                }
            } catch (err) {
                logger.error(err, 'Failed to load files in Box');
                if (!cancelled) {
                    setError('Unable to load files from Box.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [open, opportunityId]);

    return (
        <Modal
            open={open}
            title="Files in Box"
            onCancel={onClose}
            onOk={onClose}
            footer={[
                <Button key="close" type="primary" onClick={onClose}>
                    Close
                </Button>,
            ]}
            width={800}
            styles={{ body: { overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' } }}
        >
            <table className="tab__table">
                <thead>
                    <tr>
                        <th align="center">Sl Number</th>
                        <th align="left">File name</th>
                        <th align="center">version</th>
                        <th align="left">Time</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={4} align="center">
                                <Spin className="doctable_spin" />
                            </td>
                        </tr>
                    ) : error ? (
                        <tr>
                            <td colSpan={4} align="center">
                                {error}
                            </td>
                        </tr>
                    ) : files.length === 0 ? (
                        <tr>
                            <td colSpan={4} align="center">
                                No files found in Box
                            </td>
                        </tr>
                    ) : (
                        files.map((file, index) => (
                            <tr key={file.Id || index}>
                                <td align="center">{index + 1}</td>
                                <td align="left">{file.Name}</td>
                                <td align="center">{boxFileVersion(file.Etag)}</td>
                                <td align="left">{formatLocalTime(file.ModifiedUtc)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </Modal>
    );
};

export default BoxFilesModal;
